import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useDataScope } from "@/hooks/useDataScope";
import StatusBadge from "@/components/StatusBadge";
import ClaimsCard from "@/components/ClaimsCard";
import FileClaimDialog from "@/components/FileClaimDialog";
import { Info, Truck, Package, CheckCircle2, Clock, MapPin, ExternalLink, Loader2, TrendingUp, ShieldAlert } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/format";

// --- Constants ---
const SUMMARY_WINDOW_DAYS = 30;
const RECENT_SHIPMENTS_COUNT = 8;
const RECENT_ORDERS_COUNT = 6;
const IN_TRANSIT_STATUSES = ["in_transit", "out_for_delivery", "pre_transit"];
const AWAITING_STATUSES = ["awaiting_shipment", "awaiting_payment", "on_hold"];

const TRACKING_URLS = {
  UPS: (t) => `https://www.ups.com/track?tracknum=${t}`,
  FedEx: (t) => `https://www.fedex.com/fedextrack/?trknbr=${t}`,
  USPS: (t) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${t}`,
  DHL: (t) => `https://www.dhl.com/en/express/tracking.html?AWB=${t}`,
};

function trackingHref(carrier, tracking) {
  if (!tracking) return null;
  const fn = TRACKING_URLS[carrier] || TRACKING_URLS[(carrier || "").toUpperCase()];
  if (fn) return fn(tracking);
  return `https://www.google.com/search?q=${encodeURIComponent(`${carrier || ""} ${tracking}`)}`;
}

function computeSummary(orders, shipments) {
  const start = new Date();
  start.setDate(start.getDate() - SUMMARY_WINDOW_DAYS);

  const shippedInWindow = shipments.filter((s) => s.ship_date && new Date(s.ship_date) >= start);
  // Ship-date based: distinct orders that SHIPPED in the window (not orders placed).
  const ordersShipped = new Set(shippedInWindow.map((s) => s.order_number).filter(Boolean)).size || shippedInWindow.length;
  // Prefer live EasyPost delivery status (last 7d window) over raw ShipStation status.
  const inTransit = shipments.filter((s) => IN_TRANSIT_STATUSES.includes(s.delivery_status)).length;
  const delivered = shipments.filter((s) => s.delivery_status === "delivered").length;
  const awaiting = orders.filter((o) => AWAITING_STATUSES.includes(o.status)).length;

  const carrierCounts = shippedInWindow.reduce((acc, s) => {
    const c = s.carrier_display || s.carrier || "Other";
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const carrierTotal = Object.values(carrierCounts).reduce((a, b) => a + b, 0) || 1;
  const carrierBreakdown = Object.entries(carrierCounts)
    .map(([carrier, count]) => ({ carrier, count, pct: Math.round((count / carrierTotal) * 100) }))
    .sort((a, b) => b.count - a.count);

  const totalWeight = shippedInWindow.reduce((acc, s) => acc + (Number(s.weight_lbs) || 0), 0);
  const avgWeight = shippedInWindow.length ? totalWeight / shippedInWindow.length : 0;

  return {
    ordersLast30: ordersShipped,
    shippedLast30: shippedInWindow.length,
    inTransit,
    delivered,
    awaitingFulfillment: awaiting,
    carrierBreakdown,
    totalWeight,
    avgWeight,
  };
}

export default function OrdersShipments() {
  const { param, allMode, accountId } = useDataScope();
  const [orders, setOrders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [demo, setDemo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [claimOrder, setClaimOrder] = useState(null);
  const [claimsReloadKey, setClaimsReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = param ? `?${param}` : "";
    (async () => {
      try {
        const [o, s] = await Promise.all([api.get(`/orders${qs}`), api.get(`/shipments${qs}`)]);
        if (cancelled) return;
        setOrders(o.data.orders);
        setShipments(s.data.shipments);
        setDemo(o.data.uses_demo_data || s.data.uses_demo_data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [param]);

  const summary = useMemo(() => computeSummary(orders, shipments), [orders, shipments]);
  const recentShipments = shipments.slice(0, RECENT_SHIPMENTS_COUNT);
  const recentOrders = orders.slice(0, RECENT_ORDERS_COUNT);

  return (
    <div data-testid="orders-shipments-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Orders &amp; Shipments</h1>
        <p className="text-slate-500 mt-1">A digest of your recent fulfillment activity.</p>
      </div>

      {demo && (
        <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-4 py-3">
          <Info className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <strong>Demo data shown.</strong> Your live orders and shipments will appear here once your account is connected.
          </div>
        </div>
      )}

      <SummaryTiles summary={summary} loading={loading} />

      <CostPerOrderTrendCard param={param} allMode={allMode} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RecentShipmentsCard shipments={recentShipments} loading={loading} />
        </div>
        <CarrierMixCard breakdown={summary.carrierBreakdown} />
      </div>

      <RecentOrdersCard orders={recentOrders} loading={loading} allMode={allMode} onFileClaim={setClaimOrder} />

      <ClaimsCard param={param} accountId={allMode ? null : accountId} allMode={allMode} reloadKey={claimsReloadKey} />

      {claimOrder && (
        <FileClaimDialog
          order={claimOrder}
          accountId={allMode ? null : accountId}
          onClose={() => setClaimOrder(null)}
          onSubmitted={() => { setClaimOrder(null); setClaimsReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}

// ---------- Sub-components ----------
function CostPerOrderTrendCard({ param, allMode }) {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (allMode) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/billing/trend?months=6${param ? `&${param}` : ""}`)
      .then(({ data }) => { if (!cancelled) setPoints(data.enabled ? (data.points || []) : null); })
      .catch(() => { if (!cancelled) setPoints(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [param, allMode]);

  if (allMode) return null;

  const hasData = points && points.some((p) => p.order_count > 0);

  return (
    <div data-testid="cost-per-order-trend" className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
          <TrendingUp className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Cost per Order Trend</h2>
          <p className="text-xs text-slate-500">Average billed cost per order shipped, over the last 6 months.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-slate-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Loading trend…</div>
      ) : !hasData ? (
        <p className="text-center text-sm text-slate-400 py-10">Not enough billing history to show a trend yet.</p>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={points} margin={{ top: 8, right: 12, left: -6, bottom: 0 }}>
            <defs>
              <linearGradient id="cpoFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#406CB4" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#406CB4" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F0" vertical={false} />
            <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#7A8E9B" }} axisLine={false} tickLine={false} tickFormatter={(m) => (m || "").split(" ")[0]?.slice(0, 3)} />
            <YAxis tick={{ fontSize: 11, fill: "#7A8E9B" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `$${v}`} />
            <RTooltip content={<CostTip />} />
            <Area type="monotone" dataKey="avg_per_order_total" stroke="#406CB4" strokeWidth={2.5} fill="url(#cpoFill)" dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function CostTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-md px-3 py-2 shadow-lg">
      <div className="font-semibold">{p.month_label}</div>
      <div className="text-blue-300 font-bold">${p.avg_per_order_total} / order</div>
      <div className="text-slate-300 mt-0.5">{p.order_count} orders · ${p.grand_total} total</div>
    </div>
  );
}

function SummaryTiles({ summary, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-tiles-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse" />
            <div className="h-8 w-16 bg-slate-100 rounded mt-3 animate-pulse" />
            <div className="h-4 w-28 bg-slate-100 rounded mt-2 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <SummaryTile icon={Package} accent="blue" label={`Orders shipped (last ${SUMMARY_WINDOW_DAYS} days)`} value={summary.ordersLast30} sub={`${summary.awaitingFulfillment} awaiting fulfillment`} testId="summary-orders" />
      <SummaryTile icon={Truck} accent="orange" label={`Shipped (last ${SUMMARY_WINDOW_DAYS} days)`} value={summary.shippedLast30} sub={`${summary.avgWeight.toFixed(1)} lbs avg / shipment`} testId="summary-shipped" />
      <SummaryTile icon={Clock} accent="violet" label="In Transit" value={summary.inTransit} sub="Live tracking · last 7 days" testId="summary-in-transit" />
      <SummaryTile icon={CheckCircle2} accent="green" label="Delivered" value={summary.delivered} sub="Live tracking · last 7 days" testId="summary-delivered" />
    </div>
  );
}

function RecentShipmentsCard({ shipments, loading }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Recent Shipments</h2>
        <span className="text-xs text-slate-500">Showing latest {shipments.length}</span>
      </div>
      <RecentShipmentsBody shipments={shipments} loading={loading} />
    </div>
  );
}

function RecentShipmentsBody({ shipments, loading }) {
  if (loading) return <div className="py-10 text-center text-slate-500">Loading…</div>;
  if (shipments.length === 0) return <div className="py-10 text-center text-slate-500">No shipments yet.</div>;
  return (
    <ul className="divide-y divide-slate-100" data-testid="recent-shipments">
      {shipments.map((s) => <ShipmentRow key={s.id} shipment={s} />)}
    </ul>
  );
}

function ShipmentRow({ shipment: s }) {
  return (
    <li data-testid={`shipment-row-${s.id}`} className="py-3 flex items-center gap-4">
      <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
        <Truck className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm">{s.order_number}</span>
          <span className="text-slate-400">·</span>
          <span className="text-sm text-slate-600">{s.carrier_display || s.carrier} {s.service}</span>
          {s.delivery_status ? <StatusBadge status={s.delivery_status} /> : <StatusBadge status={s.status} />}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.ship_to}</span>
          <span>{formatDateTime(s.ship_date)}</span>
          {s.delivery_status === "delivered" && s.delivered_at && (
            <span className="flex items-center gap-1 text-green-600 font-medium">
              <CheckCircle2 className="w-3 h-3" /> Delivered {formatDate(s.delivered_at)}
            </span>
          )}
          {s.delivery_status && s.delivery_status !== "delivered" && s.est_delivery_date && (
            <span className="text-slate-500">Est. {formatDate(s.est_delivery_date)}</span>
          )}
          {s.tracking_number && (
            <a
              data-testid={`track-${s.id}`}
              href={trackingHref(s.carrier_display || s.carrier, s.tracking_number)}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-700 font-mono inline-flex items-center gap-1"
            >
              {s.tracking_number}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
      {s.weight_lbs != null && (
        <div className="text-xs font-semibold text-slate-500 shrink-0">{Number(s.weight_lbs).toFixed(1)} lbs</div>
      )}
    </li>
  );
}

function CarrierMixCard({ breakdown }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>
        Carrier Mix ({SUMMARY_WINDOW_DAYS}d)
      </h2>
      {breakdown.length === 0 ? (
        <p className="text-sm text-slate-500">No shipments yet.</p>
      ) : (
        <ul className="space-y-3" data-testid="carrier-breakdown">
          {breakdown.map((c) => (
            <li key={c.carrier} data-testid={`carrier-${c.carrier}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-900">{c.carrier}</span>
                <span className="text-slate-500">{c.count} · {c.pct}%</span>
              </div>
              <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${c.pct}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentOrdersCard({ orders, loading, allMode, onFileClaim }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Recent Orders</h2>
        <span className="text-xs text-slate-500">Showing latest {orders.length}</span>
      </div>
      {loading ? (
        <div className="py-10 text-center text-slate-500">Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="recent-orders-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 px-3 font-medium">Order #</th>
                <th className="py-2 px-3 font-medium">Date</th>
                <th className="py-2 px-3 font-medium">Customer</th>
                <th className="py-2 px-3 font-medium">Ship To</th>
                <th className="py-2 px-3 font-medium">Units</th>
                <th className="py-2 px-3 font-medium text-center">Status</th>
                {!allMode && <th className="py-2 px-3 font-medium text-right">Claim</th>}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-3 px-3 text-sm font-semibold text-slate-900">{o.order_number}</td>
                  <td className="py-3 px-3 text-sm text-slate-600">{formatDate(o.order_date)}</td>
                  <td className="py-3 px-3 text-sm text-slate-700">{o.customer_name}</td>
                  <td className="py-3 px-3 text-sm text-slate-600">{o.ship_to}</td>
                  <td className="py-3 px-3 text-sm text-slate-700">{o.items_count}</td>
                  <td className="py-3 px-3 text-center"><StatusBadge status={o.status} /></td>
                  {!allMode && (
                    <td className="py-3 px-3 text-right">
                      <button
                        data-testid={`file-claim-${o.id}`}
                        onClick={() => onFileClaim?.(o)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-400 rounded-md px-2.5 py-1.5 transition-colors"
                      >
                        <ShieldAlert className="w-3.5 h-3.5" /> File claim
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-3 italic">
        This page is a high-level summary of your recent orders and shipments.
      </p>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, sub, accent = "blue", testId }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-600",
    green: "bg-green-50 text-green-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tones[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-3xl font-bold text-slate-900 mt-3" style={{ fontFamily: "Manrope" }}>{value}</p>
      <p className="text-sm font-medium text-slate-700 mt-1">{label}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}
